import React, { Suspense } from "react";
import { useTheme } from "../../ThemeContext";

type MarkdownProps = {
  text: string;
};

const ReactMarkdown = React.lazy(async () => {
  const mod = await import("react-markdown");
  return { default: mod.default };
});

const remarkGfmPromise: Promise<{ default: any }> = import("remark-gfm");

const syntaxHighlighterPromise = import(
  "react-syntax-highlighter/dist/esm/prism-async-light"
).then((m) => m.default);

const darkThemePromise = import(
  "react-syntax-highlighter/dist/esm/styles/prism/one-dark"
).then((m) => m.default);

const lightThemePromise = import(
  "react-syntax-highlighter/dist/esm/styles/prism/one-light"
).then((m) => m.default);

type MarkdownRendererProps = {
  text: string;
  remarkGfm: any;
  SyntaxHighlighter: any;
  syntaxTheme: any;
};

const codeBlockStyle: React.CSSProperties = {
  margin: "0.75em 0",
  borderRadius: 6,
  border: "1px solid var(--border-subtle)",
  fontSize: 13,
};

function MarkdownRenderer({
  text,
  remarkGfm,
  SyntaxHighlighter,
  syntaxTheme,
}: MarkdownRendererProps) {
  return (
    <ReactMarkdown
      remarkPlugins={[remarkGfm]}
      urlTransform={(uri) => (uri.startsWith("blob:") ? uri : uri)}
      components={
        SyntaxHighlighter && syntaxTheme
          ? {
              code({ className, children, ...rest }) {
                const match = /language-(\w+)/.exec(className || "");
                const codeString = String(children).replace(/\n$/, "");
                const isInline =
                  !match &&
                  !codeString.includes("\n") &&
                  codeString.length < 200;

                if (isInline) {
                  return (
                    <code className={className} {...rest}>
                      {children}
                    </code>
                  );
                }

                return (
                  <SyntaxHighlighter
                    style={syntaxTheme}
                    language={match?.[1] || "text"}
                    PreTag="div"
                    customStyle={codeBlockStyle}
                    showLineNumbers={codeString.split("\n").length > 4}
                    lineNumberStyle={{
                      minWidth: "2.5em",
                      paddingRight: "1em",
                      color: "var(--text-faint)",
                      userSelect: "none" as const,
                    }}
                  >
                    {codeString}
                  </SyntaxHighlighter>
                );
              },
            }
          : undefined
      }
    >
      {text}
    </ReactMarkdown>
  );
}

export default function Markdown({ text }: MarkdownProps) {
  return (
    <Suspense fallback={<>{text}</>}>
      <AsyncMarkdown text={text} />
    </Suspense>
  );
}

function AsyncMarkdown({ text }: { text: string }) {
  const { theme } = useTheme();
  const [remarkGfm, setRemarkGfm] = React.useState<any>(null);
  const [SyntaxHighlighter, setSyntaxHighlighter] =
    React.useState<any>(null);
  const [darkTheme, setDarkTheme] = React.useState<any>(null);
  const [lightTheme, setLightTheme] = React.useState<any>(null);

  React.useEffect(() => {
    let cancelled = false;

    remarkGfmPromise.then((m) => {
      if (!cancelled) setRemarkGfm(() => m.default);
    });
    syntaxHighlighterPromise.then((m) => {
      if (!cancelled) setSyntaxHighlighter(() => m);
    });
    darkThemePromise.then((m) => {
      if (!cancelled) setDarkTheme(m);
    });
    lightThemePromise.then((m) => {
      if (!cancelled) setLightTheme(m);
    });

    return () => {
      cancelled = true;
    };
  }, []);

  if (!remarkGfm) {
    return <>{text}</>;
  }

  const syntaxTheme = theme === "light" ? lightTheme : darkTheme;

  return (
    <MarkdownRenderer
      text={text}
      remarkGfm={remarkGfm}
      SyntaxHighlighter={SyntaxHighlighter}
      syntaxTheme={syntaxTheme}
    />
  );
}
